import React, { useEffect, useState, useCallback } from 'react';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { APP_VERSION } from '../appVersion';
import api from '../utils/api';

/**
 * Polls the backend's /api/version every 5 minutes and shows a "tap to
 * refresh" pill in the bottom-right corner when the server has a newer
 * deployed build than what's running in the user's tab.
 *
 * Catches the case where:
 *  - A new bundle has been deployed
 *  - The user's PWA service worker hasn't picked it up yet (or auto-update
 *    failed silently)
 *  - The user is sitting on a stale tab and missing new features
 *
 * On click: forcibly unregister the SW and hard-reload so the next page
 * load fetches the new bundle from the server.
 */
export const VersionBanner = () => {
  const [serverVersion, setServerVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const checkVersion = useCallback(async () => {
    try {
      const { data } = await api.get('/version', {
        // Bypass any HTTP cache layer so we always see the latest server response.
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (data && data.version) {
        setServerVersion(data.version);
      }
    } catch {
      // Silent fail — banner is a UX enhancement, not a critical path.
    }
  }, []);

  useEffect(() => {
    // First check on mount — handles the user landing on a stale tab fresh.
    checkVersion();
    // Then poll every 5 minutes.
    const id = setInterval(checkVersion, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [checkVersion]);

  const stale = serverVersion && serverVersion !== APP_VERSION;
  if (!stale || dismissed) return null;

  const handleRefresh = async () => {
    // Wipe service worker so the next request fetches a fresh bundle from
    // the network instead of the cached one.
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
    } catch {
      // Best effort; even if SW unregister fails, the location.reload(true)
      // below should still bypass the HTTP cache and grab fresh assets.
    }
    window.location.reload();
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 px-3 py-2 bg-[#FF9500] text-black rounded-full shadow-2xl text-xs font-bold animate-pulse"
      data-testid="version-banner"
      role="alert"
    >
      <ArrowsClockwise size={14} weight="bold" />
      <span>New version available</span>
      <button
        onClick={handleRefresh}
        className="ml-1 px-2 py-0.5 bg-black/15 hover:bg-black/25 rounded-full transition-colors"
        data-testid="version-banner-refresh-btn"
      >
        Tap to refresh
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="ml-1 text-black/60 hover:text-black"
        title="Dismiss"
        data-testid="version-banner-dismiss-btn"
      >
        ×
      </button>
    </div>
  );
};

export default VersionBanner;
